import { Card } from "@/shared/components/ui/card"
import { Shield, BarChart3, HeartPulse, CalendarCheck, FileCheck, Lock, Headphones } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom" // 👈 Importante

const benefits = [
  {
    icon: Shield,
    title: "Validación Segura de tus Facturas Electrónicas",
    description:
      'Asegura la integridad y cumplimiento normativo de tus facturas electrónicas. Nuestro sistema de validación avanzada verifica cada detalle de tu documento, garantizando que cumpla con todos los requisitos legales y técnicos. Evita multas y rechazos por errores en tus facturas."',
    cta: "Saber más",
    link: "/servicios"
  },
  {
    icon: CalendarCheck,
    title: "Tu agenda médica, organizada y siempre a tiempo.",
    description:
      "Organiza y administra tus citas médicas de manera rápida y eficiente. Nuestra plataforma permite programar, modificar y gestionar tus agendas desde cualquier dispositivo, optimizando el tiempo de atención tanto para el profesional como para el paciente.",
    cta: "Saber más",
    link: "/servicios",
  },
  {
    icon: HeartPulse,
    title: "Tu gestión médica, más simple, segura y digital.",
    description:
      '"Optimiza la gestión de tus pacientes con nuestro sistema de historias clínicas digitales adaptables. Diseñado para profesionales independientes, IPS y EPS, te permite administrar, almacenar y consultar la información médica de forma rápida, segura y centralizada.',
    cta: "Saber más",
    link: "/servicios",
  },
]

const additionalBenefits = [
  {
    icon: Headphones,
    title: "Soporte Técnico Especializado",
    description:
      "Nuestro equipo de expertos está listo para ayudarte. Ya sea con problemas técnicos o asesoría en facturación y archivos JSON.",
  },
  {
    icon: FileCheck,
    title: "Validación Automatizada de Archivos JSON",
    description:
      "Garantiza la integridad de tus datos con nuestra herramienta de validación JSON. Detecta y corrige errores para un procesamiento sin problemas.",
  },
  {
    icon: BarChart3,
    title: "Reportes y Análisis en Tiempo Real",
    description:
      "Analiza tu facturación y datos JSON en tiempo real. Genera reportes detallados para mejorar tu gestión y toma de decisiones.",
  },
  {
    icon: FileCheck,
    title: "Integración Simplificada",
    description:
      "Nuestra plataforma facilita la importación y exportación de datos JSON, mejorando la eficiencia y simplificando su uso para todos.",
  },
  {
    icon: Lock,
    title: "Seguridad y Privacidad",
    description:
      "Tu seguridad es nuestra prioridad. Protegemos tu información con medidas avanzadas para evitar accesos no autorizados y pérdidas.",
  },
  {
    icon: FileCheck,
    title: "Gestión de Facturación",
    description:
      "Optimiza tu facturación con nuestra plataforma. Crea, gestiona y cumple con normativas fácilmente, reduciendo el tiempo en tareas administrativas.",
  },
]

export function BenefitsGrid() {
  const [visibleCards, setVisibleCards] = useState<number[]>([])
  const cardRefs = useRef<(HTMLDivElement | null)[]>([])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = cardRefs.current.indexOf(entry.target as HTMLDivElement)
            if (index !== -1 && !visibleCards.includes(index)) {
              setVisibleCards((prev) => [...prev, index])
            }
          }
        })
      },
      { threshold: 0.1 },
    )

    cardRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref)
    })

    return () => observer.disconnect()
  }, [visibleCards])

  return (
    <>
      {/* Sección principal */}
      <section className="py-20 lg:py-32 bg-background relative overflow-hidden">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />

        <div className="container relative mx-auto px-4 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-20 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold mb-6 text-balance leading-tight">
              <span style={{ color: "#06A1FF" }}>Simplifica</span>{" "}
               y <span style={{ color: "#06A1FF" }}>Optimiza</span> tu facturación electrónica con <span className="text-gradient">Rapyfact</span>
            </h2>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Te ofrecemos una solución eficiente para el sector salud, cumpliendo con las regulaciones locales y
              validando formatos JSON. Con Rapyfact, ahorra tiempo, reduce errores y optimiza tu gestión.
            </p>
          </div>

          {/* Beneficios principales */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[index] = el
                }}
                className={visibleCards.includes(index) ? "animate-scale-in" : "opacity-0"}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card className="p-8 space-y-6 h-full hover:shadow-2xl hover:shadow-primary/10 transition-all duration-500 hover:scale-105 hover:border-primary/30 group">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                    <benefit.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-xl sm:text-2xl font-bold text-balance leading-tight">{benefit.title}</h3>
                    <p className="text-base text-muted-foreground text-pretty leading-relaxed">{benefit.description}</p>
                  </div>

                  {/* Enlace al servicio */}
                  <Link to={benefit.link}>
                    <Button
                      variant="link"
                      className="text-primary p-0 h-auto group-hover:translate-x-2 transition-transform duration-300"
                    >
                      {benefit.cta} →
                    </Button>
                  </Link>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sección adicional */}
      <section className="py-20 lg:py-32 bg-gradient-to-b from-secondary/30 to-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="text-center max-w-4xl mx-auto mb-20 animate-fade-in">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-balance leading-tight">
              La mejor Selección para tu <span className="text-gradient">facturación</span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {additionalBenefits.map((benefit, index) => (
              <div
                key={index}
                ref={(el) => {
                  cardRefs.current[benefits.length + index] = el
                }}
                className={visibleCards.includes(benefits.length + index) ? "animate-scale-in" : "opacity-0"}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <Card className="p-6 space-y-4 h-full hover:shadow-xl hover:shadow-primary/10 transition-all duration-500 hover:scale-105 hover:border-primary/30 group">
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors duration-300">
                    <benefit.icon className="w-7 h-7 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg sm:text-xl font-bold text-balance">{benefit.title}</h3>
                    <p className="text-sm sm:text-base text-muted-foreground text-pretty leading-relaxed">
                      {benefit.description}
                    </p>
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
